<template>
  <div>
    <div class="mural-drawer__backdrop" @click="$emit('close')"></div>
    <div class="mural-drawer">
      <!-- Cabeçalho -->
      <div class="mural-drawer__head" :style="{ background: tintColor, borderBottomColor: color }">
        <div class="flex items-center" style="gap: .6rem;">
          <b-badge pill :style="{ background: color, color: '#fff' }">{{ lesson.modality_name }}</b-badge>
          <b-badge pill :variant="statusVariant">{{ $t(`mural.status.${status}`) }}</b-badge>
          <span class="flex-grow"></span>
          <feather-icon icon="XIcon" size="20" class="cursor-pointer" @click="$emit('close')" />
        </div>
        <h3 class="mural-drawer__title">{{ lesson.name }}</h3>
        <div class="mural-drawer__when">{{ dateLabel }} · {{ slot.startTime }} – {{ slot.endTime }}</div>

        <!--
          Atalho para o painel de check-in de ingresso — sempre visível quando a
          aula exige ingresso, independente de a sessão já ter passado ou de ela
          estar cancelada: o relatório de quem entrou continua valendo mesmo
          depois que a ação de fazer chamada some do rodapé.
        -->
        <button
          v-if="showTicketReport"
          type="button"
          class="mural-drawer__ticket-link"
          @click="goToTicketReport">
          <feather-icon icon="CheckSquareIcon" size="13" class="mr-25" />
          {{ $t('mural.actions.ticket_report') }}
        </button>
      </div>

      <!-- Corpo -->
      <div class="mural-drawer__body">
        <div class="mural-drawer__cols">
          <div>
            <div class="mural-drawer__caption">{{ $t('mural.drawer.teacher') }}</div>
            <div class="flex items-center" style="gap: .5rem;">
              <feather-icon icon="UserIcon" size="16" class="text-primary" />
              <span class="mural-drawer__strong">{{ teacherName }}</span>
            </div>
          </div>
          <div v-if="lesson.rental_space">
            <div class="mural-drawer__caption">{{ $t('mural.drawer.space') }}</div>
            <div class="flex items-center" style="gap: .5rem;">
              <feather-icon icon="MapPinIcon" size="16" class="text-danger" />
              <span class="mural-drawer__strong">{{ lesson.rental_space }}</span>
            </div>
          </div>
          <div v-else>
            <div class="mural-drawer__caption">{{ $t('mural.drawer.capacity_source') }}</div>
            <div class="flex items-center" style="gap: .5rem;">
              <feather-icon icon="SlidersIcon" size="16" class="text-warning" />
              <span class="mural-drawer__strong">{{ capacitySourceLabel }}</span>
            </div>
          </div>
        </div>

        <!-- Ocupação -->
        <div class="mt-2">
          <div class="flex justify-between items-baseline mb-25">
            <span class="mural-drawer__caption mb-0">{{ $t('mural.drawer.occupancy') }}</span>
            <span class="mural-drawer__strong">{{ occupancyText }}</span>
          </div>
          <div class="mural-drawer__track">
            <div :style="{ width: `${percent}%`, background: color }"></div>
          </div>
          <div class="text-muted mt-25"><small>{{ freeText }} · {{ mixText }}</small></div>
        </div>

        <!-- Justificativa da exceção -->
        <div v-if="slot.reason" class="mural-drawer__note">
          <strong>{{ $t('mural.drawer.reason') }}:</strong> {{ slot.reason }}
        </div>

        <!-- Alunos -->
        <div class="mt-2">
          <div class="flex justify-between items-center mb-50">
            <span class="mural-drawer__caption mb-0">{{ $t('mural.drawer.students') }}</span>
            <b-badge variant="light-secondary" pill>{{ slot.students.length }}</b-badge>
          </div>

          <div v-if="!slot.students.length && !grants.length" class="text-muted py-1">
            <small>{{ $t('mural.drawer.no_students') }}</small>
          </div>

          <div v-for="student in slot.students" :key="student.scheduled_event_id" class="mural-drawer__student">
            <span class="mural-drawer__avatar" :style="toneStyle(student.name)">{{ initialsOf(student.name) }}</span>
            <div class="mural-drawer__student-main">
              <div class="mural-drawer__student-name">{{ student.name }}</div>
              <!-- Quem já passou pela portaria — mesmo dado que o painel de
                   check-in detalha, aqui só o resumo para não sair do drawer. -->
              <div v-if="student.gate_check_in" class="mural-drawer__gate">
                <feather-icon icon="CheckSquareIcon" size="11" class="mr-25" />
                {{ $t('mural.checkin.gate_entry', { time: student.gate_check_in.at, channel: student.gate_check_in.channel_label }) }}
              </div>
              <div v-else class="text-muted"><small>{{ student.email }}</small></div>
            </div>
            <b-badge :variant="originVariant(student.origin)" pill class="mural-drawer__tag">
              {{ student.origin_label || '—' }}
            </b-badge>
            <feather-icon
              :icon="student.status === 'DO' ? 'CheckCircleIcon' : 'ClockIcon'"
              size="15"
              :class="student.status === 'DO' ? 'text-success' : 'text-warning'" />
          </div>

          <!-- Autorizações nominais: quem pode entrar acima do limite mesmo sem
               ter comprado ainda. -->
          <div v-for="grant in grants" :key="`g-${grant.id}`" class="mural-drawer__student">
            <span class="mural-drawer__avatar" :style="toneStyle(grant.user_name)">
              {{ initialsOf(grant.user_name) }}
            </span>
            <div class="mural-drawer__student-main">
              <div class="mural-drawer__student-name">{{ grant.user_name }}</div>
              <div class="text-muted"><small>{{ $t('mural.drawer.granted') }} · {{ grant.scope_label }}</small></div>
            </div>
            <b-badge variant="light-warning" pill class="mural-drawer__tag">
              {{ $t('mural.actions.grant') }}
            </b-badge>
          </div>
        </div>
      </div>

      <!-- Ações -->
      <div v-if="showActions" class="mural-drawer__foot">
        <b-button v-if="can.checkin" variant="success" block @click="$emit('action', 'checkin')">
          <feather-icon icon="UserCheckIcon" size="16" class="mr-50" />
          {{ $t('mural.actions.checkin') }}
        </b-button>

        <div class="mural-drawer__grid">
          <b-button v-if="can.reschedule" variant="outline-primary" @click="$emit('action', 'reschedule')">
            <feather-icon icon="CalendarIcon" size="15" class="mr-25" />
            {{ $t('mural.actions.reschedule') }}
          </b-button>
          <b-button v-if="can.edit" variant="outline-warning" @click="$emit('action', 'capacity')">
            <feather-icon icon="UsersIcon" size="15" class="mr-25" />
            {{ $t('mural.actions.capacity') }}
          </b-button>
          <b-button v-if="can.grant" variant="outline-primary" @click="$emit('action', 'grant')">
            <feather-icon icon="UserPlusIcon" size="15" class="mr-25" />
            {{ $t('mural.actions.grant') }}
          </b-button>
          <b-button
            v-if="can.edit"
            variant="outline-dark"
            @click="$emit('action', slot.blocked ? 'unblock' : 'block')">
            <feather-icon :icon="slot.blocked ? 'UnlockIcon' : 'SlashIcon'" size="15" class="mr-25" />
            {{ slot.blocked ? $t('mural.actions.unblock') : $t('mural.actions.block') }}
          </b-button>
        </div>

        <b-button v-if="can.cancel" variant="flat-secondary" size="sm" block @click="$emit('action', 'cancel')">
          <feather-icon icon="XCircleIcon" size="14" class="mr-25" />
          {{ $t('mural.actions.cancel') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge, BButton } from 'bootstrap-vue'
import { inkOf, isElapsed, longDateLabel, parseDate, slotStatus, tint } from './muralAgenda'

const AVATAR_TONES = [
  ['var(--tint-primary)', '#5f87b3'],
  ['var(--tint-success)', '#1d9153'],
  ['var(--tint-warning)', '#c67728'],
  ['var(--tint-info)', '#0794a8'],
  ['var(--tint-danger)', '#c9484a'],
]

/**
 * Detalhe da ocorrência e porta de entrada das ações.
 *
 * As ações são recortadas por permissão, não por papel: o admin da Hubee acumula
 * todos os papéis, então checar por nome de papel esconderia botões dele. Na
 * prática o professor sai com check-in e remarcação — vagas, bloqueio,
 * autorização e cancelamento pedem permissões que o papel `teacher` não tem.
 */
export default {
  name: 'MuralOccurrenceDrawer',
  components: { BBadge, BButton },
  props: {
    date: { type: String, required: true },
    slot: { type: Object, required: true },
    lesson: { type: Object, required: true },
  },
  computed: {
    status() {
      return slotStatus(this.slot)
    },
    statusVariant() {
      return {
        cancelled: 'light-danger',
        blocked: 'light-dark',
        full: 'light-warning',
        scheduled: 'light-primary',
      }[this.status]
    },
    color() {
      if (this.status === 'cancelled') return '#EA5455'
      if (this.status === 'blocked') return '#4B4B4B'
      return this.lesson.color || '#8BB2DB'
    },
    tintColor() {
      return tint(this.color, 0.12)
    },
    ink() {
      return inkOf(this.color)
    },
    dateLabel() {
      return longDateLabel(parseDate(this.date))
    },
    teacherName() {
      const teacher = (this.lesson.teachers || [])[0]
      return teacher ? teacher.name : this.$t('mural.drawer.no_teacher')
    },
    capacitySourceLabel() {
      return {
        override: this.$t('lesson.agenda.source_override'),
        schedule: this.$t('lesson.agenda.source_schedule'),
        lesson: this.$t('lesson.agenda.source_lesson'),
      }[this.slot.capacity_source] || this.$t('lesson.agenda.source_lesson')
    },
    grants() {
      return this.slot.seat_grants || []
    },
    /**
     * O botão só aparece com o ingresso ligado e para quem tem gestão de
     * ingressos — mesma permissão que abre o painel: `CheckinPanel` recusa
     * quem não é dono do item (o professor, que tem só `tickets.validate`, não
     * entra aqui — ele valida na porta, não lê a carteira de compradores).
     */
    showTicketReport() {
      return !!(this.lesson && this.lesson.ticket_enabled) && this.$acl.hasPermission('tickets.grid')
    },
    percent() {
      if (!this.slot.capacity) return 0
      return Math.min(100, Math.round((this.slot.occupied / this.slot.capacity) * 100))
    },
    occupancyText() {
      if (this.slot.capacity === null) return this.$t('mural.drawer.unlimited')
      return this.$t('mural.drawer.occupancy_of', {
        booked: this.slot.occupied,
        capacity: this.slot.capacity,
      })
    },
    freeText() {
      if (this.slot.capacity === null) return this.$t('mural.drawer.unlimited')
      const free = Math.max(0, this.slot.capacity - this.slot.occupied)
      if (free === 0) return this.$t('mural.drawer.full')
      if (free === 1) return this.$t('mural.drawer.free_one')
      return this.$t('mural.drawer.free_many', { count: free })
    },
    mixText() {
      return this.$t('mural.drawer.mix', {
        subscription: this.slot.occupied_subscription || 0,
        single: this.slot.occupied_single || 0,
      })
    },
    /**
     * Uma sessão cancelada é registro histórico: reabrir, remarcar ou mexer nas
     * vagas dela não faz sentido, e o backend recusa de qualquer forma.
     */
    showActions() {
      if (this.status === 'cancelled') return false
      return Object.values(this.can).some(Boolean)
    },
    can() {
      const elapsed = isElapsed(this.date, this.slot.startTime)

      return {
        // Check-in só depois que a aula começou — antes disso não há presença
        // para registrar.
        checkin: elapsed
          && this.slot.students.length > 0
          && this.$acl.hasPermission('teacher_my_lessons.confirm'),
        reschedule: !elapsed
          && this.slot.students.length > 0
          && this.$acl.hasPermission('teacher_my_lessons.reschedule'),
        edit: !elapsed && this.$acl.hasPermission('lessons.edit'),
        grant: !elapsed && this.$acl.hasPermission('lessons.seat_grant'),
        cancel: !elapsed && this.$acl.hasPermission('cancellation_requests.edit'),
      }
    },
  },
  methods: {
    /**
     * Vai para o painel de check-in já filtrado no dia desta ocorrência — quem
     * clica aqui está olhando a aula de terça, não quer cair em "hoje" e ter
     * que trocar a data de novo.
     */
    goToTicketReport() {
      this.$router
        .push(`/ingressos/checkin/lesson/${this.lesson.id}?date=${this.date}`)
        .catch(() => {})
    },
    initialsOf(name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '--'
      return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase()
    },
    toneStyle(name) {
      let hash = 0
      const value = String(name || '')
      for (let i = 0; i < value.length; i++) {
        hash = (hash * 31 + value.charCodeAt(i)) % 997
      }
      const [background, color] = AVATAR_TONES[hash % AVATAR_TONES.length]
      return { background, color }
    },
    originVariant(origin) {
      return {
        AS: 'light-success',
        RP: 'light-warning',
        MN: 'light-dark',
        AV: 'light-primary',
      }[origin] || 'light-primary'
    },
  },
}
</script>

<style lang="scss" scoped>
.mural-drawer {
  position: fixed;
  z-index: 1051;
  background: #fff;
  box-shadow: var(--shadow-dropdown);
  display: flex;
  flex-direction: column;
  right: 0;
  top: 0;
  bottom: 0;
  width: 430px;
  max-width: 100vw;

  &__backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 1050;
  }

  &__head {
    padding: 1rem 1.25rem 1.1rem;
    border-bottom: 3px solid;
  }

  &__title {
    margin: 0.6rem 0 0.2rem;
    font-size: 1.28rem;
    font-weight: var(--fw-semibold);
    color: var(--text-heading);
  }

  &__when {
    font-size: 0.857rem;
    color: var(--text-body);
  }

  &__ticket-link {
    display: inline-flex;
    align-items: center;
    margin-top: 0.6rem;
    padding: 0.3rem 0.65rem;
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: var(--radius-pill);
    background: rgba(255, 255, 255, 0.35);
    color: var(--text-heading);
    font-size: 0.76rem;
    font-weight: var(--fw-semibold);
    cursor: pointer;

    &:hover {
      background: rgba(255, 255, 255, 0.55);
    }
  }

  &__gate {
    display: flex;
    align-items: center;
    color: #28c76f;
    font-size: 0.78rem;
  }

  &__body {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem;
  }

  &__cols {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.9rem;
  }

  &__caption {
    font-size: 0.7rem;
    font-weight: var(--fw-semibold);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    margin-bottom: 0.35rem;
  }

  &__strong {
    font-size: 0.875rem;
    font-weight: var(--fw-semibold);
    color: var(--text-heading);
  }

  &__track {
    height: 8px;
    border-radius: var(--radius-pill);
    background: var(--surface-sunken);

    > div {
      height: 100%;
      border-radius: var(--radius-pill);
    }
  }

  &__note {
    margin-top: 1.1rem;
    background: var(--tint-danger);
    border-radius: var(--radius);
    padding: 0.7rem 0.9rem;
    font-size: 0.8rem;
    color: var(--danger);
  }

  &__student {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    padding: 0.55rem 0;
    border-bottom: 1px solid var(--border-color);
  }

  &__student-main {
    flex: 1;
    min-width: 0;
  }

  &__student-name {
    font-size: 0.875rem;
    font-weight: var(--fw-semibold);
    color: var(--text-heading);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__avatar {
    width: 32px;
    height: 32px;
    flex: none;
    border-radius: var(--radius-pill);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: var(--fw-semibold);
  }

  &__tag {
    flex: none;
    font-size: 0.65rem !important;
  }

  &__foot {
    padding: 1rem 1.25rem;
    border-top: 1px solid var(--border-color);
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    background: #fff;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;
  }
}

/* Em telefone o painel vira folha de baixo: 430px fixos cobririam a tela toda
   sem dar contexto do que está por trás. */
@media (max-width: 575px) {
  .mural-drawer {
    top: auto;
    left: 0;
    width: auto;
    max-height: 86vh;
    border-radius: 16px 16px 0 0;
    overflow: hidden;
  }
}
</style>
